import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { MusicianFields } from '../components/forms/MusicianFields';
import { Loader2 } from 'lucide-react';

const musicianProfileSchema = z.object({
  genres: z.array(z.string()).min(1, 'Please select at least one genre'),
  skills: z.array(z.string()).min(1, 'Please select at least one skill'),
});

type MusicianProfileFormData = z.infer<typeof musicianProfileSchema>;

function BecomeMusician() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading, refreshProfile } = useProfile();
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<MusicianProfileFormData>({
    resolver: zodResolver(musicianProfileSchema),
    defaultValues: {
      genres: [],
      skills: [],
    },
  });

  const watchGenres = watch('genres', []);
  const watchSkills = watch('skills', []);

  // Check if user is logged in
  useEffect(() => {
    if (!profileLoading) {
      // If user is not logged in, redirect to login
      if (!user) {
        setRedirecting(true);
        navigate('/login');
        return;
      }

      // If user doesn't have a profile at all, redirect to create profile
      if (!profile) {
        setRedirecting(true);
        navigate('/create-profile');
        return;
      }
    }
  }, [user, profile, profileLoading, navigate]);

  const onSubmit = async (data: MusicianProfileFormData) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    try {
      setIsSubmitting(true);
      setError('');

      const { error } = await supabase
        .from('profiles')
        .update({
          genres: data.genres,
          skills: data.skills,
          is_musician: true,
        })
        .eq('user_id', user?.id);

      if (error) throw error;
      
      // Refresh profile to update the user menu
      await refreshProfile();
      
      navigate('/profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenreChange = (genre: string) => {
    const currentGenres = watchGenres || [];
    const newGenres = currentGenres.includes(genre)
      ? currentGenres.filter(g => g !== genre)
      : [...currentGenres, genre];
    setValue('genres', newGenres);
  };

  const handleSkillChange = (skill: string) => {
    const currentSkills = watchSkills || [];
    const newSkills = currentSkills.includes(skill)
      ? currentSkills.filter(s => s !== skill)
      : [...currentSkills, skill];
    setValue('skills', newSkills);
  };

  // Show loading while checking profile status or redirecting
  if (profileLoading || redirecting) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-light/60">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          {redirecting ? 'Redirecting...' : 'Loading...'}
        </div>
      </div>
    );
  }

  // Don't render the form if user is not logged in or doesn't have a profile
  if (!user || !profile) {
    return null;
  }

  return (
    <div className="container mx-auto px-6 pb-8 md:py-12">
      <Title>{t('auth.becomeMusician.title')}</Title>

      <div className="max-w-2xl mx-auto">
        <div className="flex items-center space-x-4 mb-12">
          <div className="flex-1">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-gray-custom/10 text-light flex items-center justify-center font-semibold">
                ✓
              </div>
              <div className="ml-3">
                <h3 className="font-medium text-light">{t('auth.becomeMusician.step1')}</h3>
                <p className="text-sm text-light/60">{t('auth.becomeMusician.step1Subtitle')}</p>
              </div>
            </div>
            <div className="mt-2 h-1 bg-secondary rounded-full" />
          </div>

          <div className="flex-1">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-secondary text-light flex items-center justify-center font-semibold">
                2
              </div>
              <div className="ml-3">
                <h3 className="font-medium text-light">{t('auth.becomeMusician.step2')}</h3>
                <p className="text-sm text-light/60">{t('auth.becomeMusician.step2Subtitle')}</p>
              </div>
            </div>
            <div className="mt-2 h-1 bg-secondary rounded-full" />
          </div>
        </div>

        <div className="panel">
          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <MusicianFields
              genres={watchGenres}
              skills={watchSkills}
              onGenreChange={handleGenreChange}
              onSkillChange={handleSkillChange}
            />

            {errors.genres && (
              <p className="text-sm text-red-500">{t('auth.becomeMusician.selectGenres')}</p>
            )}
            {errors.skills && (
              <p className="text-sm text-red-500">{t('auth.becomeMusician.selectSkills')}</p>
            )}

            <button 
              type="submit" 
              className="btn-secondary w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('auth.becomeMusician.completingProfile')}</span>
                </span>
              ) : (
                t('auth.becomeMusician.completeButton')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BecomeMusician;